import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { correctiveActionsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  AlertTriangle, 
  Search,
  Plus,
  Loader2,
  Calendar,
  User,
  CheckCircle2,
  Clock,
  XCircle,
  ChevronRight,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const statusConfig = {
  open: { label: 'Open', icon: AlertTriangle, className: 'badge-error', color: 'red' },
  in_progress: { label: 'In Progress', icon: Clock, className: 'badge-warning', color: 'amber' },
  pending_approval: { label: 'Pending Approval', icon: Clock, className: 'badge-info', color: 'blue' },
  closed: { label: 'Closed', icon: CheckCircle2, className: 'badge-success', color: 'emerald' },
};

const ActionCard = ({ action, onClick }) => {
  const status = statusConfig[action.status] || statusConfig.open;
  const StatusIcon = status.icon;
  const isOverdue = action.due_date && new Date(action.due_date) < new Date() && action.status !== 'closed';
  const displayTitle = action.title || action.description?.substring(0, 100) || 'Untitled Action';

  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-all ${isOverdue ? 'border-l-4 border-l-red-500' : ''}`}
      onClick={onClick}
      data-testid={`action-card-${action.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
            isOverdue ? 'bg-red-100' : 
            action.status === 'closed' ? 'bg-emerald-100' : 'bg-amber-100'
          }`}>
            <StatusIcon className={`w-5 h-5 ${
              isOverdue ? 'text-red-600' : 
              action.status === 'closed' ? 'text-emerald-600' : 'text-amber-600'
            }`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <Badge className={status.className}>
                {status.label}
              </Badge>
              {action.priority && (
                <Badge variant="outline" className={
                  action.priority === 'critical' ? 'border-red-500 text-red-600' :
                  action.priority === 'high' ? 'border-orange-500 text-orange-600' :
                  'border-slate-400 text-slate-600'
                }>
                  {action.priority}
                </Badge>
              )}
              {action.source && (
                <Badge variant="outline" className="text-xs">
                  {action.source === 'daily_inspection' ? 'Inspection' : 
                   action.source === 'expiry_engine' ? 'Expiry' : action.source}
                </Badge>
              )}
              {isOverdue && (
                <Badge className="badge-error">Overdue</Badge>
              )}
            </div>
            <p className="font-semibold text-slate-900 line-clamp-2">{displayTitle}</p>
            {action.description && action.title && (
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                {action.description.substring(0, 150)}...
              </p>
            )}
            <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Due: {action.due_date ? new Date(action.due_date).toLocaleDateString() : 'N/A'}
              </span>
              {action.vehicle_registration && (
                <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">
                  {action.vehicle_registration}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
};

export const CorrectiveActionsPage = () => {
  const navigate = useNavigate();
  const { isSupervisor } = useAuth();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAction, setSelectedAction] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [closureNotes, setClosureNotes] = useState('');

  useEffect(() => {
    fetchActions();
  }, [statusFilter]);

  const fetchActions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await correctiveActionsAPI.list(params);
      setActions(response.data);
    } catch (error) {
      console.error('Failed to fetch corrective actions:', error);
      toast.error('Failed to load corrective actions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!selectedAction) return;
    
    try {
      setUpdating(true);
      await correctiveActionsAPI.update(selectedAction.id, {
        status: newStatus,
        closure_notes: newStatus === 'closed' ? closureNotes : undefined,
      });
      toast.success('Corrective action updated');
      setShowDetailDialog(false);
      setClosureNotes('');
      fetchActions();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update corrective action');
    } finally {
      setUpdating(false);
    }
  };

  const filteredActions = actions.filter(action => {
    const searchLower = searchQuery.toLowerCase();
    return (action.description || '').toLowerCase().includes(searchLower) ||
           (action.title || '').toLowerCase().includes(searchLower) ||
           (action.action_required || '').toLowerCase().includes(searchLower) ||
           (action.vehicle_registration || '').toLowerCase().includes(searchLower) ||
           (action.submission?.submission_number || '').toLowerCase().includes(searchLower);
  });

  // Stats
  const openCount = actions.filter(a => a.status === 'open').length;
  const inProgressCount = actions.filter(a => a.status === 'in_progress').length;
  const overdueCount = actions.filter(a => 
    a.due_date && new Date(a.due_date) < new Date() && a.status !== 'closed'
  ).length;
  const closedCount = actions.filter(a => a.status === 'closed').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="corrective-actions-page">
      {/* Header */}
      <div>
        <h1 
          className="text-3xl font-bold text-slate-900"
          style={{ fontFamily: 'Barlow Condensed' }}
        >
          CORRECTIVE ACTIONS
        </h1>
        <p className="text-slate-500 mt-1">
          Track and manage compliance corrective actions
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-sm flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600 font-mono">{openCount}</p>
              <p className="text-sm text-slate-500">Open</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600 font-mono">{inProgressCount}</p>
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-sm flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600 font-mono">{overdueCount}</p>
              <p className="text-sm text-slate-500">Overdue</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-sm flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600 font-mono">{closedCount}</p>
              <p className="text-sm text-slate-500">Closed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search corrective actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
            data-testid="actions-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48 h-12" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Actions List */}
      {filteredActions.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No corrective actions</h3>
          <p className="text-slate-500 mt-1">
            {statusFilter !== 'all' ? 'No actions match your filter' : 'All actions have been resolved'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredActions.map((action) => (
            <ActionCard 
              key={action.id}
              action={action}
              onClick={() => {
                setSelectedAction(action);
                setShowDetailDialog(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle 
              className="uppercase tracking-wide"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Corrective Action Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedAction && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Status</p>
                <Badge className={statusConfig[selectedAction.status]?.className}>
                  {statusConfig[selectedAction.status]?.label}
                </Badge>
              </div>
              
              {selectedAction.title && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Title</p>
                  <p className="text-slate-900 font-semibold">{selectedAction.title}</p>
                </div>
              )}
              
              <div>
                <p className="text-sm text-slate-500 mb-1">Description</p>
                <p className="text-slate-900 whitespace-pre-wrap">{selectedAction.description}</p>
              </div>
              
              {selectedAction.action_required && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Action Required</p>
                  <p className="text-slate-900">{selectedAction.action_required}</p>
                </div>
              )}
              
              {selectedAction.root_cause && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Root Cause</p>
                  <p className="text-slate-900">{selectedAction.root_cause}</p>
                </div>
              )}
              
              {selectedAction.vehicle_registration && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Vehicle</p>
                  <p className="text-slate-900 font-mono">{selectedAction.vehicle_registration}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Due Date</p>
                  <p className="text-slate-900 font-mono">
                    {selectedAction.due_date ? new Date(selectedAction.due_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                {selectedAction.submission && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Linked Submission</p>
                    <p className="text-slate-900 font-mono text-sm">
                      {selectedAction.submission.submission_number}
                    </p>
                  </div>
                )}
              </div>
              
              {selectedAction.source && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Source</p>
                  <Badge variant="outline">
                    {selectedAction.source === 'daily_inspection' ? 'Daily Inspection' : 
                     selectedAction.source === 'expiry_engine' ? 'Expiry Alert' : selectedAction.source}
                  </Badge>
                </div>
              )}
              
              {selectedAction.status !== 'closed' && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Closure Notes</p>
                  <Textarea
                    placeholder="Enter closure notes when closing this action..."
                    value={closureNotes}
                    onChange={(e) => setClosureNotes(e.target.value)}
                    rows={3}
                    data-testid="closure-notes"
                  />
                </div>
              )}
              
              {selectedAction.closure_notes && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">Closure Notes</p>
                  <p className="text-slate-900">{selectedAction.closure_notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            
            {selectedAction?.status === 'open' && (
              <Button 
                onClick={() => handleStatusUpdate('in_progress')}
                disabled={updating}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="start-progress-btn"
              >
                {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Start Progress
              </Button>
            )}
            
            {selectedAction?.status === 'in_progress' && (
              <Button 
                onClick={() => handleStatusUpdate('pending_approval')}
                disabled={updating}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="request-approval-btn"
              >
                {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Request Approval
              </Button>
            )}
            
            {(selectedAction?.status === 'pending_approval' || selectedAction?.status === 'in_progress') && isSupervisor && (
              <Button 
                onClick={() => handleStatusUpdate('closed')}
                disabled={updating || !closureNotes.trim()}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="close-action-btn"
              >
                {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Close Action
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CorrectiveActionsPage;
